import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Mail, ArrowLeft, Loader2, Eye, EyeOff } from "lucide-react";

const LOGO_URL = "/logo-infirmiers.png";

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const from = location.state?.from?.pathname || "/dashboard";

  const [emailNotVerified, setEmailNotVerified] = useState(false);
  const [resending, setResending] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setEmailNotVerified(false);

    try {
      const response = await fetch(`${API}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ email, password })
      });

      // Handle error statuses with potential body stream issues
      if (!response.ok) {
        let errorMessage = "Erreur de connexion";
        let isEmailNotVerified = false;
        
        try {
          const data = await response.json();
          if (response.status === 403 && data.detail === "email_not_verified") {
            isEmailNotVerified = true;
          } else {
            errorMessage = data.detail || errorMessage;
          }
        } catch (parseError) {
          // If body was already consumed (infrastructure issue)
          if (response.status === 403) {
            // 403 on login typically means email not verified
            isEmailNotVerified = true;
          } else if (response.status === 401) {
            errorMessage = "Identifiants invalides";
          }
        }
        
        if (isEmailNotVerified) {
          setEmailNotVerified(true);
          return;
        }
        throw new Error(errorMessage);
      }

      const data = await response.json();

      login(data.user, data.token);
      toast.success("Connexion réussie !");
      
      // Redirect based on user type
      if (data.user.user_type === "nurse") {
        navigate("/nurse/dashboard");
      } else if (data.user.user_type === "admin") {
        navigate("/admin");
      } else {
        navigate("/dashboard");
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    setResending(true);
    try {
      await fetch(`${API}/auth/resend-verification`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email })
      });
      toast.success("Email de vérification renvoyé !");
    } catch {
      toast.error("Erreur lors de l'envoi");
    } finally {
      setResending(false);
    }
  };

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-[#0D1C32] flex">
      {/* Left side - Form */}
      <div className="flex-1 flex flex-col justify-center px-4 sm:px-6 lg:px-20 py-12">
        <div className="max-w-md w-full mx-auto">
          {/* Logo centered and larger */}
          <Link to="/" className="block mb-8">
            <img src={LOGO_URL} alt="Infirmiers Privés" className="h-20 mx-auto" />
          </Link>
          
          <h1 className="text-3xl font-bold text-white mb-2 text-center">Connexion</h1>
          <p className="text-[#94A3B8] mb-8 text-center">
            Accédez à votre espace personnel
          </p>

          {/* Google Login */}
          <Button 
            type="button"
            variant="outline"
            className="w-full mb-6 bg-white text-[#0D1C32] hover:bg-gray-100 border-0"
            onClick={handleGoogleLogin}
            data-testid="google-login-btn"
          >
            <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
              <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Continuer avec Google
          </Button>

          <div className="relative mb-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-[#2A3A4D]"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-4 bg-[#0D1C32] text-[#64748B]">ou</span>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <Label htmlFor="email" className="form-label">Email</Label>
              <div className="relative">
                <Mail className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#64748B] pointer-events-none" />
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="input-dark pr-10"
                  placeholder="votre@email.com"
                  required
                  data-testid="email-input"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="password" className="form-label">Mot de passe</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="input-dark pr-10"
                  placeholder="••••••••"
                  required
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#64748B] hover:text-white transition-colors"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <div className="flex justify-end">
              <Link to="/forgot-password" className="text-sm text-[#00E073] hover:underline" data-testid="forgot-password-link">
                Mot de passe oublié ?
              </Link>
            </div>

            {emailNotVerified && (
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4">
                <p className="text-amber-400 text-sm font-medium mb-2">Email non vérifié</p>
                <p className="text-[#94A3B8] text-sm mb-3">
                  Vous devez confirmer votre adresse email avant de pouvoir vous connecter. 
                  Vérifiez votre boîte de réception.
                </p>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="border-amber-500/50 text-amber-400 hover:bg-amber-500/10"
                  onClick={handleResendVerification}
                  disabled={resending}
                  data-testid="resend-from-login-btn"
                >
                  {resending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Renvoyer l'email de vérification
                </Button>
              </div>
            )}

            <Button 
              type="submit" 
              className="btn-primary w-full" 
              disabled={loading}
              data-testid="login-submit-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Connexion...
                </>
              ) : (
                "Se connecter"
              )}
            </Button>
          </form>

          <p className="mt-8 text-center text-[#94A3B8]">
            Pas encore de compte ?{" "}
            <Link to="/register" className="text-[#00E073] hover:underline">
              Créer un compte
            </Link>
          </p>

          <Link to="/" className="block mt-6 text-center text-[#64748B] hover:text-white text-sm">
            <ArrowLeft className="w-4 h-4 inline mr-1" />
            Retour à l'accueil
          </Link>
        </div>
      </div>

      {/* Right side - Image */}
      <div className="hidden lg:block lg:w-1/2 relative">
        <div className="absolute inset-0 bg-gradient-to-r from-[#0D1C32] to-transparent z-10"></div>
        <img 
          src="https://images.unsplash.com/photo-1579684385127-1ef15d508118?auto=format&fit=crop&w=1200&q=80"
          alt="Soins infirmiers"
          className="h-full w-full object-cover"
        />
      </div>
    </div>
  );
};

export default Login;
